import type { RatioBlock, RecruitmentDashboardPayload } from '../recruitmentDashboard.types';
import {
    reportingInterviewCanonicalFromApplication,
    reportingStatusToCanonical,
} from './reportingStageCanonical';
import { getCandidateEmail } from './candidateContact';

const ACTIVE_VACANCY_STATUS = new Set(['actief', 'put_live', 'open', 'doing', 'live', 'published', 'active']);

const AWAITING_FEEDBACK_STATUS = new Set([
    'voorgesteld',
    'interview',
    'gesprek',
    '2e gesprek',
    '3e gesprek',
    'tweede gesprek',
    'derde gesprek',
    'vervolg',
    'aanbod',
    'aanbieding',
]);

const PIPELINE_CANONICAL = new Set([
    'applied',
    'shortlisted',
    'cv_sent',
    'interview_1',
    'interview_further',
    'offer',
]);

export type ReportingScopedKpiNumbers = {
    jobsAdded: number;
    shortlistsAdded: number;
    cvsSent: number;
    firstInterviews: number;
    furtherInterviews: number;
    offers: number;
    permPlacements: number;
    websiteApplications: number;
};

export type ReportingScopedMetrics = {
    live: RecruitmentDashboardPayload['live'];
    revenue: RecruitmentDashboardPayload['revenue'];
    efficiency: RecruitmentDashboardPayload['efficiency'] & { avg_time_to_hire: number | null };
    data_quality: RecruitmentDashboardPayload['data_quality'];
    ratios: RecruitmentDashboardPayload['ratios'];
    activity: {
        by_recruiter: RecruitmentDashboardPayload['activity']['by_recruiter'];
    };
};

function normStatus(s: unknown): string {
    return String(s ?? '')
        .trim()
        .toLowerCase();
}

function daysBetween(start: string, end: string): number {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    return Math.max(0, ms / 86400000);
}

function avgRounded(samples: number[]): number | null {
    if (!samples.length) return null;
    return Math.round((samples.reduce((a, b) => a + b, 0) / samples.length) * 10) / 10;
}

function pct(num: number, den: number): number | null {
    if (!den) return null;
    return Math.round((num / den) * 1000) / 10;
}

function ratioString(num: number, den: number): string | null {
    if (!den) return null;
    return `${num}:${den}`;
}

function ratioBlock(num: number, den: number): RatioBlock {
    return { ratio: ratioString(num, den), pct: pct(num, den) };
}

function isLiveVacancy(v: any): boolean {
    const s = normStatus(v?.status || v?.data?.status || v?.data?.lifecycleStatus);
    return ACTIVE_VACANCY_STATUS.has(s);
}

function canonicalFromApp(a: any): ReturnType<typeof reportingStatusToCanonical> {
    const iv = reportingInterviewCanonicalFromApplication(a);
    if (iv) return iv;
    return reportingStatusToCanonical(a?.status);
}

function placementFeeFromApp(a: any, vac?: any): number {
    const d = (a?.data && typeof a.data === 'object' ? a.data : {}) as Record<string, unknown>;
    const vd = (vac?.data && typeof vac.data === 'object' ? vac.data : {}) as Record<string, unknown>;
    const raw = d.fee ?? d.placement_fee ?? vd.fee ?? vd.placement_fee ?? d.billing_rate ?? 0;
    const fee = Number(raw);
    return Number.isFinite(fee) ? fee : 0;
}

function placementFeeFromPlacementRow(p: any, vac?: any): number {
    const d = (p?.data && typeof p.data === 'object' ? p.data : {}) as Record<string, unknown>;
    const raw =
        p?.billing_rate ??
        p?.salary ??
        d.fee ??
        d.placement_fee ??
        placementFeeFromApp({ data: d }, vac);
    const fee = Number(raw);
    return Number.isFinite(fee) ? fee : 0;
}

function hasCvOrCoreFields(a: any): boolean {
    const cand = a?.candidates;
    const c = Array.isArray(cand) ? cand[0] : cand;
    if (String(a?.cv_summary || '').trim().length > 10) return true;
    const apd = c?.ai_parsed_data;
    if (apd && typeof apd === 'object') {
        if ((apd as Record<string, unknown>).cv_url || (apd as Record<string, unknown>).resume) return true;
    }
    const snap = a?.cv_snapshot;
    if (snap && typeof snap === 'object' && Object.keys(snap as object).length > 0) return true;
    return false;
}

export function computeReportingScopedMetrics(input: {
    filteredApps: any[];
    filteredAppsAllTime: any[];
    filteredVacancies: any[];
    /** Rijen uit `public.placements` in scope + periode (created_at). */
    placementsInPeriod?: any[];
    recruiters: Array<{ id: string; name?: string; email?: string }>;
    rawKpi: ReportingScopedKpiNumbers;
    vacanciesById: Map<string, any>;
    getRecruiterIdFromApp: (app: any) => string | null;
    idleThresholdDays?: number;
}): ReportingScopedMetrics {
    const {
        filteredApps,
        filteredAppsAllTime,
        filteredVacancies,
        placementsInPeriod = [],
        recruiters,
        rawKpi,
        vacanciesById,
        getRecruiterIdFromApp,
        idleThresholdDays = 14,
    } = input;

    const active_vacancies = filteredVacancies.filter(isLiveVacancy).length;

    const candidates_in_interview = filteredAppsAllTime.filter((a) => {
        const c = canonicalFromApp(a);
        return c === 'interview_1' || c === 'interview_further';
    }).length;

    const candidates_in_pipeline = filteredAppsAllTime.filter((a) => {
        const c = canonicalFromApp(a);
        return c != null && c !== 'placement' && PIPELINE_CANONICAL.has(c);
    }).length;

    const candidates_awaiting_feedback = filteredAppsAllTime.filter((a) =>
        AWAITING_FEEDBACK_STATUS.has(normStatus(a?.status)),
    ).length;

    const placementApps = filteredApps.filter((a) => canonicalFromApp(a) === 'placement');
    let revenueSum = 0;
    const placementRows =
        placementsInPeriod.length > 0
            ? placementsInPeriod
            : placementApps.map((a) => ({ application: a }));
    for (const row of placementRows) {
        const p = row?.application ? null : row;
        const a = row?.application ?? row;
        const vac =
            vacanciesById.get(String((p?.vacancy_id ?? a?.vacancy_id) || '')) ||
            (Array.isArray(p?.vacancy) ? p.vacancy[0] : p?.vacancy) ||
            (Array.isArray(a?.vacancies) ? a.vacancies[0] : a?.vacancies);
        revenueSum += p ? placementFeeFromPlacementRow(p, vac) : placementFeeFromApp(a, vac);
    }
    const placements_count =
        placementsInPeriod.length > 0 ? placementsInPeriod.length : placementApps.length;
    revenueSum = Math.round(revenueSum * 100) / 100;

    const firstInterviewDays: number[] = [];
    const placementDays: number[] = [];
    const stageDeltas: number[] = [];

    for (const a of filteredApps) {
        const created = a.created_at;
        if (!created) continue;
        const iv = reportingInterviewCanonicalFromApplication(a);
        if (iv === 'interview_1' || iv === 'interview_further') {
            const pm = (a.cv_snapshot as Record<string, unknown> | undefined)?.pipeline_meta as
                | Record<string, unknown>
                | undefined;
            const end = String(pm?.gesprek_scheduled_at || a.updated_at || '');
            if (end) firstInterviewDays.push(daysBetween(created, end));
        }
        if (canonicalFromApp(a) === 'placement' && a.updated_at) {
            placementDays.push(daysBetween(created, a.updated_at));
        }
        if (a.updated_at && a.updated_at !== created) {
            stageDeltas.push(daysBetween(created, a.updated_at));
        }
    }

    const emails = new Map<string, number>();
    let candidates_missing_cv = 0;
    let candidates_missing_email_or_phone = 0;
    const seenCandidate = new Set<string>();

    for (const a of filteredAppsAllTime) {
        const cid = String(a.candidate_id || '').trim();
        if (!cid || seenCandidate.has(cid)) continue;
        seenCandidate.add(cid);
        const cand = Array.isArray(a.candidates) ? a.candidates[0] : a.candidates;
        if (!hasCvOrCoreFields(a)) candidates_missing_cv += 1;
        const email = getCandidateEmail(cand);
        const phone = String(cand?.phone || '').trim();
        if (!email && !phone) candidates_missing_email_or_phone += 1;
        if (email) {
            const key = email.toLowerCase();
            emails.set(key, (emails.get(key) || 0) + 1);
        }
    }

    const duplicate_emails_detected = [...emails.values()].filter((n) => n > 1).length;

    const idleCutoff = Date.now() - idleThresholdDays * 86400000;
    let idleCount = 0;
    for (const a of filteredAppsAllTime) {
        const cand = Array.isArray(a.candidates) ? a.candidates[0] : a.candidates;
        const last = a.updated_at || cand?.updated_at || cand?.added_at;
        if (last && new Date(last).getTime() < idleCutoff) idleCount += 1;
    }

    const k = rawKpi;
    const totalInterviews = k.firstInterviews + k.furtherInterviews;
    const rJobsShort = ratioBlock(k.jobsAdded, k.shortlistsAdded);
    const rShortCv = ratioBlock(k.shortlistsAdded, k.cvsSent);
    const rCvIv = ratioBlock(k.cvsSent, totalInterviews);
    const rIvOffer = ratioBlock(totalInterviews, k.offers);
    const rOfferPlace = ratioBlock(k.offers, k.permPlacements);

    const activityMap = new Map<string, RecruitmentDashboardPayload['activity']['by_recruiter'][0]>();
    for (const r of recruiters) {
        activityMap.set(r.id, {
            recruiter_id: r.id,
            name: r.name || r.email || 'Recruiter',
            new_candidates_added: 0,
            cvs_sent: 0,
            interviews_scheduled: 0,
            calls_messages_logged: 0,
            activity_score: 0,
        });
    }
    for (const a of filteredApps) {
        const rid = getRecruiterIdFromApp(a);
        if (!rid) continue;
        let row = activityMap.get(rid);
        if (!row) {
            row = {
                recruiter_id: rid,
                name: rid,
                new_candidates_added: 0,
                cvs_sent: 0,
                interviews_scheduled: 0,
                calls_messages_logged: 0,
                activity_score: 0,
            };
            activityMap.set(rid, row);
        }
        row.new_candidates_added += 1;
        if (canonicalFromApp(a) === 'cv_sent') row.cvs_sent += 1;
        const iv = reportingInterviewCanonicalFromApplication(a);
        if (iv) row.interviews_scheduled += 1;
    }
    const by_recruiter = Array.from(activityMap.values())
        .map((row) => ({
            ...row,
            activity_score:
                row.new_candidates_added * 2 + row.cvs_sent * 3 + row.interviews_scheduled * 4 + row.calls_messages_logged,
        }))
        .filter((row) => row.activity_score > 0 || row.new_candidates_added > 0)
        .sort((a, b) => (b.activity_score ?? 0) - (a.activity_score ?? 0));

    const avgPlacement = avgRounded(placementDays);

    return {
        live: {
            active_vacancies,
            candidates_in_pipeline,
            candidates_in_interview,
            candidates_awaiting_feedback,
        },
        revenue: {
            placements_count,
            revenue_generated: revenueSum,
            avg_revenue_per_placement:
                placements_count > 0 ? Math.round((revenueSum / placements_count) * 100) / 100 : null,
            by_client: {},
            by_recruiter: {},
        },
        efficiency: {
            avg_days_to_first_interview: avgRounded(firstInterviewDays),
            avg_days_to_placement: avgPlacement,
            avg_days_between_stages: avgRounded(stageDeltas),
            avg_time_to_hire: avgPlacement != null ? Math.round(avgPlacement) : null,
        },
        data_quality: {
            candidates_missing_cv,
            candidates_missing_email_or_phone,
            duplicate_emails_detected,
            candidates_idle_days: { threshold_days: idleThresholdDays, count: idleCount },
        },
        ratios: {
            jobs_to_shortlists: rJobsShort,
            shortlists_to_cv_sent: rShortCv,
            cv_sent_to_interview: rCvIv,
            interview_to_offer: rIvOffer,
            offer_to_placement: rOfferPlace,
            conversion_chain: {
                jobs_to_shortlists: rJobsShort,
                shortlists_to_cv_sent: rShortCv,
                cv_sent_to_interview: rCvIv,
                interview_to_offer: rIvOffer,
                offer_to_placement: rOfferPlace,
            },
        },
        activity: { by_recruiter },
    };
}
